import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { getOwnedFamilyChildren } from "../services/family-registration.api";
import type { FamilyChild } from "../types/domain";

interface FamilyScopeContextValue {
  childrenList: FamilyChild[];
  activeChildId: string | null;
  activeChild: FamilyChild | null;
  loading: boolean;
  error: string;
  setActiveChildId: (id: string) => void;
  refreshChildren: () => Promise<void>;
}

const FamilyScopeContext = createContext<FamilyScopeContextValue | null>(null);

export function FamilyScopeProvider({ children }: { children: ReactNode }) {
  const { user, activePersona } = useAuth();
  const userId = user?.id;

  const [childrenList, setChildrenList] = useState<FamilyChild[]>([]);
  const [activeChildId, setActiveChildId] = useState<string | null>(() => {
    return localStorage.getItem("wicend.erp.activeChildId");
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isFamily = activePersona === "PARENT";

  const refreshChildren = useCallback(async () => {
    if (!userId || !isFamily) {
      setLoading(false);
      setChildrenList([]);
      return;
    }
    // Only show full loading state if we do not have children loaded yet.
    // Subsequent revalidations happen quietly in the background without UI flicker.
    if (childrenList.length === 0) {
      setLoading(true);
    }
    setError("");
    try {
      const data = await getOwnedFamilyChildren(userId);
      setChildrenList(data);
      if (data.length > 0) {
        // If current active is not in the list, set to the first one
        if (!activeChildId || !data.some((c) => c.id === activeChildId)) {
          setActiveChildId(data[0].id);
        }
      } else {
        setActiveChildId(null);
      }
    } catch (cause: unknown) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Data keluarga belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, [userId, isFamily, activeChildId, childrenList.length]);

  useEffect(() => {
    void refreshChildren();
  }, [refreshChildren]);

  useEffect(() => {
    if (activeChildId) {
      localStorage.setItem("wicend.erp.activeChildId", activeChildId);
    } else {
      localStorage.removeItem("wicend.erp.activeChildId");
    }
  }, [activeChildId]);

  const activeChild = useMemo(
    () => childrenList.find((c) => c.id === activeChildId) ?? null,
    [childrenList, activeChildId],
  );

  const value = useMemo<FamilyScopeContextValue>(
    () => ({
      childrenList,
      activeChildId,
      activeChild,
      loading,
      error,
      setActiveChildId,
      refreshChildren,
    }),
    [childrenList, activeChildId, activeChild, loading, error, refreshChildren],
  );

  return (
    <FamilyScopeContext.Provider value={value}>
      {children}
    </FamilyScopeContext.Provider>
  );
}

// oxlint-disable-next-line react/only-export-components
export const useFamilyScope = (): FamilyScopeContextValue => {
  const context = useContext(FamilyScopeContext);
  if (!context) {
    throw new Error(
      "useFamilyScope harus dipakai di dalam FamilyScopeProvider.",
    );
  }
  return context;
};
